import { createContext, useCallback, useContext, useEffect, useMemo, useRef, ReactNode } from 'react'

type BackGuard = () => boolean

interface SidebarBackGuardContextValue {
  registerBackGuard: (guard: BackGuard) => () => void
  isBackGuarded: () => boolean
}

/**
 * Default for trees rendered outside the provider (e.g. `SharedAppShellLayout`,
 * which renders its own `Sidebar` pair): never guarded, register is a no-op.
 */
const NO_GUARD: SidebarBackGuardContextValue = {
  registerBackGuard: () => () => {},
  isBackGuarded: () => false,
}

const SidebarBackGuardContext = createContext<SidebarBackGuardContextValue>(NO_GUARD)

/**
 * Lets a route rendered inside the app shell veto sidebar-initiated navigation
 * away from itself. The shell renders the sidebar *above* the router outlet, so
 * a page cannot pass its dirty state down as a prop; it registers a predicate
 * here instead. React Router is mounted as `BrowserRouter`, so `useBlocker`
 * (data router only) is unavailable.
 *
 * The predicate is held in a ref so registering never re-renders the shell.
 */
export function SidebarBackGuardProvider({ children }: { children: ReactNode }) {
  const guardRef = useRef<BackGuard | null>(null)

  const registerBackGuard = useCallback((guard: BackGuard) => {
    guardRef.current = guard
    return () => {
      // Identity check: a newly mounted route may have replaced this guard
      // before the outgoing route's cleanup runs.
      if (guardRef.current === guard) {
        guardRef.current = null
      }
    }
  }, [])

  const isBackGuarded = useCallback(() => guardRef.current?.() === true, [])

  const value = useMemo<SidebarBackGuardContextValue>(
    () => ({ registerBackGuard, isBackGuarded }),
    [registerBackGuard, isBackGuarded],
  )

  return (
    <SidebarBackGuardContext.Provider value={value}>
      {children}
    </SidebarBackGuardContext.Provider>
  )
}

export function useSidebarBackGuardControls() {
  return useContext(SidebarBackGuardContext)
}

/**
 * Guards sidebar-initiated navigation for as long as the calling component is
 * mounted. Mirrors `useStudyCreationBeforeUnload(shouldWarn)`, which covers the
 * tab-close case from the same predicate.
 */
export function useSidebarBackGuard(shouldGuard: boolean) {
  const { registerBackGuard } = useSidebarBackGuardControls()
  const shouldGuardRef = useRef(shouldGuard)

  useEffect(() => {
    shouldGuardRef.current = shouldGuard
  }, [shouldGuard])

  useEffect(
    () => registerBackGuard(() => shouldGuardRef.current),
    [registerBackGuard],
  )
}
